import { InteractionRequiredAuthError } from '@azure/msal-browser';
import type { LoaderFunction } from 'react-router';

import { startLoginRedirect } from './interactiveAuth';
import { loginRequest } from './msalConfig';
import msalInstance from './msalInstance';

/**
 * Prevents protected route loaders from fetching data without a usable
 * account - a cached account whose token can no longer be silently
 * refreshed (expired session, revoked consent) is treated the same as no
 * account at all, mirroring the check `AuthContext`'s own `refresh()` makes.
 */
export const authenticatedLoader =
  (loader: LoaderFunction): LoaderFunction =>
  async args => {
    const cachedAccounts = msalInstance.getAllAccounts();
    const account =
      msalInstance.getActiveAccount() ??
      (cachedAccounts.length === 1 ? (cachedAccounts[0] ?? null) : null);

    const redirectToLogin = (): null => {
      void startLoginRedirect(msalInstance, loginRequest).catch((error: unknown) => {
        console.error('[auth] Failed to start login before loading protected data.', error);
      });
      return null;
    };

    if (!account) {
      return redirectToLogin();
    }

    if (!msalInstance.getActiveAccount()) {
      msalInstance.setActiveAccount(account);
    }

    try {
      await msalInstance.acquireTokenSilent({ ...loginRequest, account });
    } catch (error: unknown) {
      // Only an account that genuinely needs interaction (expired session,
      // revoked consent, MFA required) should be torn down - a transient
      // network/service failure doesn't mean the cached session is bad, so
      // destroying it would force an unnecessary login on a retry-able blip.
      if (!(error instanceof InteractionRequiredAuthError)) {
        console.error(
          '[auth] Transient failure acquiring an access token silently; leaving the session intact.',
          error
        );
        throw error;
      }

      console.error(
        '[auth] Cached account requires interactive login; clearing the session.',
        error
      );
      await msalInstance.clearCache();
      msalInstance.setActiveAccount(null);
      return redirectToLogin();
    }

    return loader(args);
  };
